import { useToken } from 'react-bootstrap-typeahead';
import { ChipCombinedProps, ChipErrorEnum, ChipStateEnum } from './types';
import React, { MouseEventHandler, useState, MouseEvent, useEffect } from 'react';
import { isEmptyString } from '../../../lib/utils';
import { useSingleAndDoubleClick } from './utils';

type ChipPresenterProps = ChipCombinedProps & {
  inputValue?: string;
  onKeyDown?: (e) => void;
  onInputChanged?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onClickHandler?:(event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  onBlur?: () => void;
  divOnKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const usePresenter = (props: ChipCombinedProps): ChipPresenterProps => {
  const {
    mutableChipProps,
    onTextChanged,
  } = props;
  const [editableState, setEditableState] = useState<ChipStateEnum>('Default');
  const [errorState, setErrorState] = useState<ChipErrorEnum>('None');
  
  useEffect(() => {
    if (mutableChipProps) {
      const { onValidate, option, onChangeChipValue, index } = mutableChipProps;
      if (onValidate && onValidate(option.value) === false) {
        setErrorState('Error');
      } else {
        setErrorState('None');
      }
      if (onChangeChipValue && (index || index === 0)) {
        onChangeChipValue(index, {
          value: option.value,
          isValid: errorState === 'None',
        });
      }
    }
  }, [mutableChipProps?.option, errorState]);
  
  if (mutableChipProps) {
    const { 
      option,
      tokenProps,
      index,
      onRemoveChip,
      onChangeChipValue,
      shouldEditOnEnter = true,
    } = mutableChipProps;
    const [inputValue, setInputValue] = React.useState<string>(option.value);

    const saveChipValue = () => {
      if (isEmptyString(inputValue)) {
        if (onRemoveChip) {
          onRemoveChip(index);
        }
      } else {
        if (onChangeChipValue && (index || index === 0)) { 
          onChangeChipValue(index, {
            value: inputValue,
            isValid: errorState === 'None',
          });
        }
      }
      setEditableState('Default');
    };

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setInputValue(event.target.value);
      if (onTextChanged) {
        onTextChanged(event.target.value);
      }
    };
  
    const onSingleClickHandler: MouseEventHandler<HTMLDivElement> = () => {
      // no-op
    };

    const editChip = () => {
      setInputValue(option.value);
      setEditableState('Edit');
    };

    const divOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (
        e.key !== 'Enter' || 
        !shouldEditOnEnter || 
        // If focus is something else, such as the X
        document.activeElement !== e.currentTarget
      ) {
        return;
      }

      e.stopPropagation();
      editChip();
    };
  
    const onDoubleClickHandler: MouseEventHandler<HTMLDivElement> = editChip;
  
    const onClickHandler = useSingleAndDoubleClick(
      onSingleClickHandler,
      onDoubleClickHandler,
    );

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case 'Escape':
          saveChipValue();
          break;
        default:
          break;
      }
    };

    const onBlur = () => {
      saveChipValue();
    };
    
    if (option && (index || index === 0)){
      const { ...otherProps } = useToken({ option, ...tokenProps });
      return {
        ...props,
        ...otherProps,
        inputValue: inputValue,
        text: {
          value: option.value,
        },
        state: editableState,
        error: errorState,
        button: {
          onClick: () => onRemoveChip && onRemoveChip(index),
        },
        onKeyDown: onKeyDown,
        onInputChanged: onInputChanged,
        onClickHandler: onClickHandler,
        onBlur: onBlur,
        divOnKeyDown,
      };
    }
  }

  return {
    ...props,
  };
};

export default usePresenter;
